import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Check, X } from 'lucide-react';
import { api } from '../../lib/api';
import { t } from '../../lib/i18n';
import { formatDateTime } from '../../lib/format';
import { ErrorLine } from '../../components/ErrorPanel';
import { Alert, Button } from '../../components/ui';
import { BASE, MAX_PDF_BYTES, PublicVerifyResult } from './api';

function Row({ label, ok, text }: { label: string; ok: boolean | null; text: string }) {
  const Icon = ok ? Check : X;
  return (
    <li className="flex items-start gap-3 py-2">
      {ok === null ? <span aria-hidden="true" className="h-5 w-5 shrink-0" /> : <Icon aria-hidden="true" className={`mt-0.5 h-5 w-5 shrink-0 ${ok ? 'text-green-800' : 'text-red-800'}`} />}
      <div>
        <div className="font-medium">
          {label}: {ok === null ? t('agreementsUi.verify.skipped') : ok ? t('agreementsUi.verify.passed') : t('agreementsUi.verify.failed')}
        </div>
        <div className="text-sm text-slate-700">{text}</div>
      </div>
    </li>
  );
}

/**
 * Public "verify a PDF" page. It needs no sign-in and uses nothing from the signed-in portal (no
 * session, tenant or site); it shows only the verification result the server returns.
 */
export function PublicVerifyPage() {
  const [file, setFile] = useState<File | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const check = useMutation({
    mutationFn: (f: File) => {
      const form = new FormData();
      form.append('file', f);
      return api.post(`${BASE}/public/verify`, { form, schema: PublicVerifyResult });
    },
  });

  const choose = (f: File | undefined) => {
    check.reset();
    setFile(null);
    if (!f) {
      setProblem(null);
      return;
    }
    if (f.type !== 'application/pdf' && !f.name.toLowerCase().endsWith('.pdf')) return setProblem(t('agreementsUi.public.notPdf'));
    if (f.size > MAX_PDF_BYTES) return setProblem(t('agreementsUi.public.tooBig'));
    setProblem(null);
    setFile(f);
  };

  const r = check.data;
  const genuine = r ? r.found && r.signatureValid && r.chainVerified === true : false;
  return (
    <main className="mx-auto min-h-screen max-w-xl px-4 py-8">
      <h1 className="text-2xl font-semibold">{t('agreementsUi.public.title')}</h1>
      <p className="mt-2 text-slate-700">{t('agreementsUi.public.intro')}</p>
      <form
        className="mt-6 space-y-4"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          if (!file) return setProblem(t('agreementsUi.public.noFile'));
          check.mutate(file);
        }}
      >
        <div>
          <label htmlFor="verify-file" className="mb-1 block text-sm font-medium">
            {t('agreementsUi.public.file')}
          </label>
          <input
            id="verify-file"
            type="file"
            accept="application/pdf,.pdf"
            className="block w-full text-sm"
            aria-invalid={problem ? true : undefined}
            aria-describedby={problem ? 'verify-file-err' : undefined}
            onChange={(e) => choose(e.target.files?.[0])}
          />
          {problem ? (
            <p id="verify-file-err" role="alert" className="mt-1 text-sm text-red-800">
              {problem}
            </p>
          ) : null}
        </div>
        <Button type="submit" variant="primary" disabled={check.isPending}>
          {check.isPending ? t('agreementsUi.public.checking') : t('agreementsUi.public.check')}
        </Button>
      </form>
      <div className="mt-4">
        <ErrorLine error={check.error} />
      </div>
      {r ? (
        <section aria-label={t('agreementsUi.public.result')} className="mt-6 rounded-lg border border-slate-200 p-4">
          <Alert kind={genuine ? 'success' : 'error'}>{genuine ? t('agreementsUi.public.genuine') : t('agreementsUi.public.notGenuine')}</Alert>
          <ul className="mt-2 divide-y divide-slate-100">
            <Row label={t('agreementsUi.public.found')} ok={r.found} text={r.found ? t('agreementsUi.public.found.yes') : t('agreementsUi.public.found.no')} />
            <Row
              label={t('agreementsUi.public.signature')}
              ok={r.signatureValid}
              text={r.signatureValid ? t('agreementsUi.public.signature.yes') : t('agreementsUi.public.signature.no')}
            />
            <Row
              label={t('agreementsUi.public.chain')}
              ok={r.chainVerified}
              text={
                r.chainVerified === null
                  ? t('agreementsUi.public.chain.na')
                  : r.chainVerified
                    ? t('agreementsUi.public.chain.yes', { when: formatDateTime(r.chainVerifiedAt) })
                    : t('agreementsUi.public.chain.no')
              }
            />
          </ul>
          {r.sealedAt ? <p className="mt-2 text-sm">{t('agreementsUi.public.sealed', { when: formatDateTime(r.sealedAt) })}</p> : null}
        </section>
      ) : null}
      <p className="mt-6 text-sm">
        <a className="text-brand underline" href="/api/v1/agreements/public/certificate" download>
          {t('agreementsUi.public.certificate')}
        </a>
      </p>
    </main>
  );
}
